export const ADMIN_CAPABILITIES = ["finance", "management", "founder"] as const;
export type AdminCapability = (typeof ADMIN_CAPABILITIES)[number];

export const ADMIN_CAPABILITY_LABELS: Record<AdminCapability, string> = {
  finance: "Financial tooling",
  management: "Restricted management",
  founder: "Founder-only tools",
};

export interface AdminPage {
  href: string;
  label: string;
  group: string;
  capability?: AdminCapability;
  navCapability?: AdminCapability;
  outreach?: boolean;
}

function page(
  group: string,
  href: string,
  label: string,
  extra: {
    capability?: AdminCapability;
    navCapability?: AdminCapability;
    outreach?: boolean;
  } = {},
): AdminPage {
  return { group, href, label, ...extra };
}

export const ADMIN_PAGES: ReadonlyArray<AdminPage> = [
  page("Daily work", "/admin/inbox", "Inbox"),
  page("Daily work", "/admin/attention", "Needs attention"),
  page("Daily work", "/admin/inbox/drafts", "Inbox drafts"),
  page("Daily work", "/admin/inbox/sent", "Inbox sent"),
  page("Daily work", "/admin/orders", "Orders"),
  page("Daily work", "/admin/orders/checkouts", "Checkouts"),
  page("Daily work", "/admin/inbox/stalled", "Stalled pickups"),
  page("Daily work", "/admin/test-inbox", "Test inbox"),
  page("Daily work", "/admin/demo", "Customer demo"),
  page("Daily work", "/admin/quotes", "Quotes"),
  page("Daily work", "/admin/leads", "Leads"),
  page("Daily work", "/admin/sales-flow", "Follow-ups"),
  page("Daily work", "/admin/accounts", "Companies"),
  page("Daily work", "/admin/products", "Products"),
  page("Work queues", "/admin/billing/approvals", "Approvals", { capability: "finance" }),
  page("Work queues", "/admin/billing/checkout-reviews", "Paid, no order", { capability: "finance" }),
  page("Work queues", "/admin/billing/invoice-dispatch", "Invoice problems", { capability: "finance" }),
  page("CRM", "/admin/prospects", "Prospects", { outreach: true }),
  page("CRM", "/admin/prospects/scorecard", "Outreach scorecard", { outreach: true }),
  page("CRM", "/admin/contacts", "People"),
  page("CRM", "/admin/calendar", "Calendar"),
  page("CRM", "/admin/map", "Map"),
  page("CRM", "/admin/price-surveys", "Yard price surveys"),
  page("Freight", "/admin/freight", "Overview"),
  page("Freight", "/admin/freight/requests", "Requests"),
  page("Freight", "/admin/freight/shipments", "Shipments"),
  page("Freight", "/admin/freight/rates", "Rates"),
  page("Finance", "/admin/billing", "Summary", { capability: "finance" }),
  page("Finance", "/admin/billing/books", "Books", { capability: "finance" }),
  page("Finance", "/admin/billing/receivables", "Receivables", { capability: "finance" }),
  page("Finance", "/admin/billing/payables", "Payables", { capability: "finance" }),
  page("Finance", "/admin/billing/reports", "Reports", { capability: "finance" }),
  page("Finance", "/admin/billing/accounts", "Ledger accounts", { capability: "finance" }),
  page("Finance", "/admin/billing/profit", "Profit & Margin", { capability: "finance" }),
  page("Finance", "/admin/billing/expenses", "Expenses", { capability: "finance" }),
  page("Finance", "/admin/billing/cash", "Cash and runway", { capability: "finance" }),
  page("Finance", "/admin/billing/order-books", "Order books", { capability: "finance" }),
  page("Finance", "/admin/billing/settlements", "Settlements", { capability: "finance" }),
  page("Finance", "/admin/billing/documents", "Documents", { capability: "finance" }),
  page("Finance", "/admin/billing/reconciliation", "Reconciliation", { capability: "finance" }),
  page("Finance", "/admin/credit", "Credit", { capability: "finance" }),
  page("Finance", "/admin/billing/order-financials", "Order financials", { capability: "finance" }),
  page("Analytics", "/admin/analytics", "Overview"),
  page("Analytics", "/admin/analytics/customers", "Customers"),
  page("Analytics", "/admin/analytics/demand", "Demand"),
  page("Analytics", "/admin/analytics/operations", "Operations"),
  page("Analytics", "/admin/analytics/sales", "Sales"),
  page("Finance", "/admin/analytics/order-financials", "Order financials", { capability: "finance" }),
  page("Finance", "/admin/analytics/fundraising", "Fundraising metrics", { capability: "finance" }),
  page("Analytics", "/admin/ads", "Ads", { capability: "finance" }),
  page("Investors", "/admin/investors", "Overview", { capability: "founder" }),
  page("Investors", "/admin/investors/contacts", "Investors", { capability: "founder" }),
  page("Investors", "/admin/investors/updates", "Investor updates", { capability: "founder" }),
  page("Price Index", "/admin/price-index", "Price Index", { capability: "management" }),
  page("Content", "/admin/cms", "Pages", { capability: "management" }),
  page("Content", "/admin/blog", "Blog", { capability: "management" }),
  page("Content", "/admin/cms/content", "Content Library", { capability: "management" }),
  page("Content", "/admin/cms/product-types", "Product Types", { capability: "management" }),
  page("Content", "/admin/cms/cities", "Cities", { capability: "management" }),
  page("Content", "/admin/cms/states", "States", { capability: "management" }),
  page("Content", "/admin/newsletter", "Newsletter", { capability: "management" }),
  page("AI", "/admin/assistant", "Assistant", { navCapability: "founder" }),
  page("AI", "/admin/ai-console", "AI Console", { capability: "founder" }),
  page("AI", "/admin/ai-console/knowledge", "Knowledge", { capability: "founder" }),
  page("AI", "/admin/ai-console/conversations", "Conversations", { capability: "founder" }),
  page("AI", "/admin/ai-console/image-analysis", "Image analysis", { capability: "founder" }),
  page("AI", "/admin/ai-console/learning", "Learning", { capability: "founder" }),
  page("AI", "/admin/ai-console/config", "Configuration", { capability: "founder" }),
  page("AI", "/admin/ai-console/usage", "Usage", { capability: "founder" }),
  page("AI", "/admin/ai-console/quality", "Quality", { capability: "founder" }),
  page("AI", "/admin/ai-console/agents", "Agents"),
  page("Settings", "/admin/email/templates", "Email templates"),
  page("Settings", "/admin/email/signatures", "Signatures"),
  page("Settings", "/admin/email/automations", "Email automations"),
  page("Settings", "/admin/email/log", "Email log"),
  page("Settings", "/admin/email/suppressions", "Suppressions"),
  page("Settings", "/admin/email/settings", "Email settings"),
  page("Settings", "/admin/email/health", "Email health"),
  page("Settings", "/admin/email/senders", "Domains & senders"),
  page("Settings", "/admin/email/integrations", "Email integrations"),
  page("Settings", "/admin/integrations", "Integrations", { navCapability: "management" }),
  page("Settings", "/admin/integrations/quickbooks", "QuickBooks", { capability: "finance" }),
  page("Settings", "/admin/users", "Users", { capability: "management" }),
  page("Settings", "/admin/settings/access", "Page access", { capability: "founder" }),
  page("Settings", "/admin/settings/slack", "Slack"),
  page("Settings", "/admin/staff-activity", "Staff activity", { capability: "founder" }),
  page("Phone", "/admin/phone", "Calls"),
  page("Phone", "/admin/phone/voicemail", "Voicemail"),
  page("Phone", "/admin/phone/tree", "Phone tree", { capability: "management" }),
  page("Phone", "/admin/phone/teams", "Teams", { capability: "management" }),
  page("Support", "/admin/support", "Tickets"),
  page("Support", "/admin/support/projects", "Projects"),
  page("Support", "/admin/support/errors", "Errors"),
  page("Support", "/admin/support/settings", "Support settings"),
  page("Developer", "/admin/releases", "Releases", { navCapability: "management" }),
  page("Developer", "/admin/releases/pull-requests", "Pull requests", { navCapability: "management" }),
  page("Developer", "/admin/releases/history", "Release history", { navCapability: "management" }),
  page("Developer", "/admin/design-system", "Design system", { navCapability: "management" }),
  page("Developer", "/admin/debug", "Background jobs", { capability: "founder" }),
  page("Developer", "/admin/debug/crons", "Crons", { capability: "founder" }),
  page("Developer", "/admin/debug/system-logs", "System logs", { capability: "founder" }),
  page("Developer", "/admin/schema", "Schema manager", { capability: "founder" }),
  page("Developer", "/admin/schema/explorer", "Data explorer", { navCapability: "founder" }),
  page("Developer", "/admin/schema/explorer/routes", "Route registry", { navCapability: "founder" }),
  page("Developer", "/admin/schema/explorer/schemas", "Schema inspector", { navCapability: "founder" }),
];

export const RECORD_ROUTE_LIST_PAGES: Readonly<Record<string, string>> = {
  "/admin/companies": "/admin/accounts",
};

export const OUTREACH_RECORD_ROUTES: ReadonlyArray<string> = ["/admin/companies", "/admin/contacts"];

export const STAFF_PAGE_SIZE = 40;
export const PAGE_AUDIENCE_LIMIT = 60;

const PAGES_BY_HREF = new Map(ADMIN_PAGES.map((entry) => [entry.href, entry]));

export function adminPageForHref(href: string): AdminPage | undefined {
  const path = href.split(/[?#]/)[0] ?? href;
  return PAGES_BY_HREF.get(path);
}
